/**
 * React Hooks for Supabase Companies
 * Provides hooks for company data management with React Query
 */

import { useQuery, useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { 
  companyService, 
  SupabaseCompany, 
  CompanyFilters, 
  CompanyStats 
} from '@/integrations/supabase/companyService';

/**
 * Hook to fetch all companies
 */
export function useSupabaseCompanies(
  filters: CompanyFilters = {},
  enabled: boolean = true
): UseQueryResult<SupabaseCompany[], Error> {
  return useQuery({
    queryKey: ['supabase-companies', filters],
    queryFn: () => companyService.getCompanies(filters),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch a single company by ID
 */
export function useSupabaseCompany(
  id: string,
  enabled: boolean = true
): UseQueryResult<SupabaseCompany | null, Error> {
  return useQuery({
    queryKey: ['supabase-company', id],
    queryFn: () => companyService.getCompanyById(id),
    enabled: enabled && !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
}

/**
 * Hook to fetch a company by legacy ID (CompID)
 */
export function useSupabaseCompanyByLegacyId(
  legacyId: string,
  enabled: boolean = true
): UseQueryResult<SupabaseCompany | null, Error> {
  return useQuery({
    queryKey: ['supabase-company-legacy', legacyId],
    queryFn: () => companyService.getCompanyByLegacyId(legacyId),
    enabled: enabled && !!legacyId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
}

/**
 * Hook to fetch active companies only
 */
export function useActiveCompanies(
  enabled: boolean = true
): UseQueryResult<SupabaseCompany[], Error> {
  return useQuery({
    queryKey: ['supabase-companies-active'],
    queryFn: () => companyService.getActiveCompanies(),
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
}

/**
 * Hook to fetch company statistics
 */
export function useCompanyStats(
  enabled: boolean = true
): UseQueryResult<CompanyStats, Error> {
  return useQuery({
    queryKey: ['company-stats'],
    queryFn: () => companyService.getCompanyStats(),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to create a new company
 */
export function useCreateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (company: Omit<SupabaseCompany, 'id' | 'created_at' | 'updated_at'>) =>
      companyService.createCompany(company),
    onSuccess: () => {
      // Invalidate and refetch company queries
      queryClient.invalidateQueries({ queryKey: ['supabase-companies'] });
      queryClient.invalidateQueries({ queryKey: ['company-stats'] });
    },
  });
}

/**
 * Hook to update a company
 */
export function useUpdateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<SupabaseCompany> }) =>
      companyService.updateCompany(id, updates),
    onSuccess: (data) => {
      // Update the specific company query
      queryClient.setQueryData(['supabase-company', data.id], data);
      // Invalidate company list queries
      queryClient.invalidateQueries({ queryKey: ['supabase-companies'] });
      queryClient.invalidateQueries({ queryKey: ['company-stats'] });
    },
  });
}

/**
 * Hook to delete a company (soft delete)
 */
export function useDeleteCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => companyService.deleteCompany(id),
    onSuccess: () => {
      // Invalidate and refetch company queries
      queryClient.invalidateQueries({ queryKey: ['supabase-companies'] });
      queryClient.invalidateQueries({ queryKey: ['company-stats'] });
    },
  });
}

/**
 * Utility hook to get company name by legacy ID
 * Useful for display purposes when you only have CompID
 */
export function useCompanyNameByLegacyId(legacyId?: string) {
  const { data: company } = useSupabaseCompanyByLegacyId(
    legacyId || '',
    !!legacyId
  );

  return company?.company_name || legacyId || 'Unknown Company';
}

/**
 * Hook for cache invalidation utilities
 */
export function useCompaniesCache() {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: ['supabase-companies'] }),
    invalidateCompany: (id: string) =>
      queryClient.invalidateQueries({ queryKey: ['supabase-company', id] }),
    invalidateStats: () =>
      queryClient.invalidateQueries({ queryKey: ['company-stats'] }),
  };
}

// Export everything for convenience
export * from '@/integrations/supabase/companyService';